import React, { useRef, useState, useEffect } from 'react';
import './EidCard.css';

/**
 * EidCard – JPEG image with name overlaid at a per-card fixed position.
 * Props: card, name, nameColor, fontSize, fontFamily, namePos, selected, onClick, noShadow
 */
const EidCard = React.forwardRef(function EidCard(
  { card, name, nameColor, fontSize, fontFamily, namePos, selected, onClick, noShadow },
  ref
) {
  const containerRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(480);
  const pos = namePos || { x: 50, y: 85 };

  // Measure the real rendered card width so font scales correctly on any
  // screen size AND when html-to-image captures the element for download/share.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setCardWidth(entry.contentRect.width || 480);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 480px is the desktop reference size.
  // Scale proportionally but never exceed the user-chosen value.
  const scaledFontSize = Math.max(10, Math.min(fontSize || 26, Math.round((fontSize || 26) * (cardWidth / 480))));

  return (
    <div
      className={`eid-card${selected ? ' eid-card--selected' : ''}`}
      ref={(node) => {
        containerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      onClick={onClick}
    >
      <div className="eid-card-photo">
        <img src={card.src} alt={card.label} crossOrigin="anonymous" draggable={false} />

        {/* Name overlaid at the card's fixed position */}
        <div
          className="eid-name-label"
          style={{
            left: `${pos.x}%`,
            top: `${pos.y}%`,
            color: nameColor || '#ffffff',
            fontSize: `${scaledFontSize}px`,
            fontFamily: fontFamily ? `'${fontFamily}', 'Cairo', sans-serif` : "'Cairo', sans-serif",
            ...(noShadow ? { textShadow: 'none' } : {}),
          }}
        >
          {name || '...'}
        </div>
      </div>
    </div>
  );
});

export default EidCard;
